import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import * as actions from 'redux/actions';
import { Notification, ProjectEdit } from 'app/models';
import { ProjectService, AuthenticationService } from 'app/services';
import { eq } from 'lodash';

@Component({
  selector: 'app-edit-project',
  templateUrl: './edit-project.component.html',
  styles: [
    `
      .project-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
        justify-content: space-between;
      }

      .management-actions,
      .box-button-right {
        display: flex;
      }

      :host ::ng-deep .save .p-button {
        background: #465675 !important;
        border-color: transparent !important;
        box-shadow: none;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box.p-highlight {
        background: #465675 !important;
        border-color: #465675 !important;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box.p-highlight:hover {
        background: #4e5f80 !important;
        border-color: #4e5f80 !important;
      }

      :host ::ng-deep .p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box:hover {
        border-color: #465675;
      }

      :host ::ng-deep .p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box.p-focus {
        box-shadow: 0 0 0 0.2rem rgba(70, 86, 117, 0.25);
        border-color: #465675;
      }

      .shared-control {
        align-items: center;
        display: flex;
        gap: 0.5rem;
        margin-top: 0.75rem;
      }

      .shared-control label,
      .logo-field > label {
        display: block;
        margin: 0;
      }

      .logo-field > label {
        margin-bottom: 0.5rem;
      }

      .color-input-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .color-input-container input {
        min-width: 0;
      }

      .color-swatch {
        width: 2.25rem;
        height: 2.25rem;
        flex: 0 0 2.25rem;
        border: 1px solid #ced4da;
        border-radius: 4px;
      }
    `,
  ],
})
export class EditProjectComponent implements OnInit {
  public form: FormGroup;
  public isSaving: boolean;
  public projectId: number;
  public userId: number;
  public showSettingSection: boolean;
  public project: any;
  public hasMainTopic: boolean;
  public accountId: number;
  public markdownDescription: string;
  public projectImage: File;

  constructor(
    private projectService: ProjectService,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private auth: AuthenticationService
  ) {
    this.isSaving = false;
    this.showSettingSection = false;
    this.project = null;
    this.hasMainTopic = false;
    this.markdownDescription = '';
    this.projectImage = null;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: [null, Validators.required],
      description: [null],
      query: [null],
      color: [null],
      shared: [true],
    });

    this.store.select('ui').subscribe(({ showProjectSettingstSection }) => {
      this.showSettingSection = showProjectSettingstSection;
      if (showProjectSettingstSection) {
        this.form.get('name').setValue(this.project.name);
        this.form.get('description').setValue(this.project.description);
        this.form.get('query').setValue(this.project.query);
        this.form.get('color').setValue(this.project.color);
        this.form.get('shared').setValue(this.project.shared);
        this.markdownDescription = this.project.description;
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.projectId = projectSelected.value;
        this.project = projectSelected;
      }
    });

    this.store.select('projects').subscribe(({ action }) => {
      if (eq(action, 'editProjectSuccess')) {
        actions.showNotification({
          notify: new Notification(
            'Project name has been renamed',
            'success',
            'tl'
          ),
        });
        this.handleCancel();
      }

      if (eq(action, 'editProjectError')) {
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification(
              'Error renaming project name',
              'error',
              'tl'
            ),
          })
        );
      }
    });

    this.store.select('userData').subscribe(({ data }) => {
      if (data) {
        this.userId = data.userId;
        this.accountId = data.accountId;
      }
    });
  }

  invalidField(field: string) {
    return (
      this.form.controls[field].errors && this.form.controls[field].touched
    );
  }

  onMarkdownChange(markdown: string): void {
    this.markdownDescription = markdown;
    console.log('Updated markdown:', markdown);
  }

  handleSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;

    if (this.projectImage) {
      this.handleSaveProjectImage();
    }

    const projectEdit = new ProjectEdit({
      projectId: this.projectId,
      name: this.form.get('name').value,
      description: this.markdownDescription,
      query: this.form.get('query').value,
      color: this.form.get('color').value,
      shared: this.form.get('shared').value,
    });

    this.store.dispatch(actions.editProject({ projectEdit }));
  }

  public setImageFile(e) {
    this.projectImage = e;
  }

  handleSaveProjectImage() {
    this.projectService
      .uploadProjectLogo(this.projectId, this.projectImage)
      .subscribe(
        () => {},
        () => {
          this.store.dispatch(
            actions.showNotification({
              notify: new Notification(
                'Error updating project logo',
                'error',
                'tl'
              ),
            })
          );
        }
      );
  }

  handleCancel() {
    this.isSaving = false;
    this.store.dispatch(actions.hideProjectSettingsSection());
  }

  openCategoryManager() {
    this.store.dispatch(actions.showCategoriesListDialog());
  }

  openAlertSettings() {
    this.store.dispatch(actions.showAlertSettingsModal());
  }

  isAdmin() {
    return this.auth.isAdminUser();
  }
}
